import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";
import { CallState, type AppDispatch } from "@b2bportal/core-types";
import {
  initializePackages,
  type PackageSearchQueryParams,
} from "@tenant/packages";
import { RBC_PACKAGES_QUERY_PARAM } from "@tenant/types";
import { isSameRequestedSearch } from "./isSameRequestedSearch";
import { PackageSearchSelectors } from "@/store/packages/selectors";
import { getPendingChosenFlight } from "@/store/packages/selectors/basePackageSelectors";
import { PackagesBaseActions } from "@/store/packages/slice/packages";
import { abortCurrentHotelAvailabilityMore } from "@/store/packages/thunks/hotels/fetchPackageHotelAvailabilityMore";
import { PACKAGE_UP_SELL_PARAMS } from "@/tracking/upSell/packageUpSellParams";

export const usePackageHotelAvailabilityPageFetch = ({
  search,
}: {
  search: PackageSearchQueryParams;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasInitialized = useRef(false);
  // Holds the in-flight initializePackages thunk so we can abort it — and
  // stop any customize/sub-thunk dispatches — when the search changes.
  const activeInitializeRef = useRef<{ abort: () => void } | null>(null);
  const lastSearchRef = useRef<string>("");

  const requestedSearch = useSelector(
    PackageSearchSelectors.getRequestedSearch
  );
  const initializedState = useSelector(
    PackageSearchSelectors.getInitializedState
  );

  const pendingChosenFlight = useSelector(getPendingChosenFlight);

  const searchKey = useMemo(() => JSON.stringify(search), [search]);

  const isSameSearch = useMemo(
    () => isSameRequestedSearch(search, requestedSearch),
    [requestedSearch, search]
  );

  // True when up_sell=true is in the URL but the flight context is gone (e.g.
  // page refresh). Guards against starting a packageless requisition.
  // Excludes InProcess: initializePackages.pending fires synchronously before
  // clearPendingChosenFlight, so pendingChosenFlight/requestedSearch can both
  // be null mid-init — that window must not be treated as an orphaned upsell.
  const isOrphanedUpsell = useMemo(
    () =>
      searchParams.get(RBC_PACKAGES_QUERY_PARAM.upSell) === "true" &&
      pendingChosenFlight == null &&
      requestedSearch == null &&
      initializedState !== CallState.InProcess,
    [searchParams, pendingChosenFlight, requestedSearch, initializedState]
  );

  useEffect(() => {
    // If the upsell params are orphaned (flight context lost on refresh), strip
    // them and bail out — initialization will run normally once the URL re-renders
    // without the param (isOrphanedUpsell will be false on the next cycle).
    if (isOrphanedUpsell) {
      const next = new URLSearchParams(searchParams);
      PACKAGE_UP_SELL_PARAMS.forEach((param) => next.delete(param));
      navigate({ search: next.toString() }, { replace: true });
      return;
    }

    if (!isSameSearch) {
      // Deduplicate: same searchKey means this effect re-fired due to an
      // unrelated dep change (e.g. isOrphanedUpsell). We don't gate on
      // InProcess because abort() cancels in-flight work, and gating would
      // silently drop a URL change while fetchPackageSearchParams is pending.
      if (hasInitialized.current && lastSearchRef.current === searchKey) {
        return;
      }

      hasInitialized.current = true;
      lastSearchRef.current = searchKey;

      activeInitializeRef.current?.abort();

      // Clear stale pricing before loading new results
      dispatch(PackagesBaseActions.clearBenchmarkProducts());

      activeInitializeRef.current = dispatch(
        initializePackages({
          search,
          chosenFlight: pendingChosenFlight ?? undefined,
        })
      );
      dispatch(PackagesBaseActions.clearPendingChosenFlight());
    } else {
      hasInitialized.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSameSearch, searchKey, isOrphanedUpsell]);

  // Abort in-flight /customize pagination on unmount (e.g. user navigates back
  // to flights), preventing stale state updates after leaving the page.
  useEffect(() => {
    return () => {
      abortCurrentHotelAvailabilityMore();
    };
  }, []);
};
